// Copyright (c) Ping. All rights reserved.
//
// Best-effort DeviceGroup mirror for per-conversation read pointers. Read
// pointers are DeviceGroup-only personal state (§2.10 `0x9A READ_POINTER`):
// the local write through `webReadPointersPersister` is the source of truth and
// ALWAYS happens first; this helper additionally broadcasts the new read
// position to the user's OTHER devices so Activity unread state + chat unread
// badges stay consistent across devices.
//
// Mirrors `apps/web/src/features/tasks/deviceGroupTaskSync.ts`; the read pointer
// is time-based (`last_read_at`) to match the time-based read stores on each
// platform (see types-and-schemas §2.10 "Live registry note"). Best-effort —
// never throws; receivers reconcile monotonically (newest `last_read_at` wins).

import { encodeEnvelopeJsonBytes, type ReadPointerRecord } from '@ping/ui';
import { bytesToHex } from '@ping/ui/src/conversation/mlsBytes';

import { log } from '@core/log/log';
import { resolveActiveConv } from '@features/chat/resolveActiveConv';

// Local mirror of the foundations `ReadPointerBody` wire shape (§2.10
// `0x9A READ_POINTER`, time-based). `decodeReadPointer` reads these keys.
interface ReadPointerWire {
  readonly conversation_id: string;
  readonly last_read_at: number;
  readonly last_read_message_id?: string;
}

interface SdkClientLike {
  listConversations?: () => Promise<Array<{ id: Uint8Array; is_device_group: boolean }>>;
}

async function resolveDeviceGroupConvId(client: unknown): Promise<string | null> {
  const sdk = client as SdkClientLike;
  if (typeof sdk.listConversations !== 'function') return null;
  let convs: Array<{ id: Uint8Array; is_device_group: boolean }>;
  try {
    convs = await sdk.listConversations();
  } catch (err) {
    log.warn({
      event: 'readState.deviceGroup.list_failed',
      message: (err as Error).message,
    });
    return null;
  }
  const dg = convs.find((c) => c.is_device_group);
  return dg ? bytesToHex(dg.id) : null;
}

/**
 * Mirror a read pointer to the DeviceGroup. No-op (logged) when there is no
 * client or no DeviceGroup conversation yet — the local persister write the
 * caller already performed remains authoritative.
 */
export async function sendReadPointer(
  client: unknown | null,
  record: ReadPointerRecord,
): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'readState.deviceGroup.no_dg' });
      return;
    }
    const conv = await resolveActiveConv({ client, conversationId, conversationName: undefined });
    if (conv === null) return;
    const payload: ReadPointerWire = {
      conversation_id: record.conversationId,
      last_read_at: record.lastReadAt,
      ...(record.lastReadMessageId !== undefined
        ? { last_read_message_id: record.lastReadMessageId }
        : {}),
    };
    await conv.send(
      encodeEnvelopeJsonBytes({
        kind: 'com.ping.read_pointer',
        payload: payload as unknown as Record<string, unknown>,
      }),
    );
    log.info({
      event: 'readState.deviceGroup.sent',
      convPrefix: record.conversationId.slice(0, 8),
    });
  } catch (err) {
    log.warn({ event: 'readState.deviceGroup.failed', message: (err as Error).message });
  }
}
